import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, RefreshCw, Database, Download, Play } from "lucide-react";

export default function AdminSettings() {
  const { t } = useTranslation();
  return (
    <PageChrome title={t("admin.settings.title")} subtitle={t("admin.settings.subtitle")} testId="admin-settings-page">
      <Tabs defaultValue="billing">
        <TabsList>
          <TabsTrigger value="billing" data-testid="settings-tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="templates" data-testid="settings-tab-templates">Email templates</TabsTrigger>
          <TabsTrigger value="triggers" data-testid="settings-tab-triggers">Triggers</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="backups" data-testid="settings-tab-backups">Backups</TabsTrigger>
        </TabsList>
        <TabsContent value="billing"><BillingTab/></TabsContent>
        <TabsContent value="templates"><TemplatesTab/></TabsContent>
        <TabsContent value="triggers"><TriggersTab/></TabsContent>
        <TabsContent value="integrations"><HealthTab/></TabsContent>
        <TabsContent value="backups"><BackupsTab/></TabsContent>
      </Tabs>
    </PageChrome>
  );
}

function BillingTab() {
  const [c, setC] = useState({ name:"", nif:"", iban:"", bic:"", creditor_id:"" });
  const [src, setSrc] = useState("");
  useEffect(() => {
    http.get("/admin/settings/billing").then(({data}) => {
      setC({ ...c, ...(data.creditor || {}) });
      setSrc(data.active_source);
    }); // eslint-disable-next-line
  }, []);
  const save = async () => {
    await http.put("/admin/settings/billing", c);
    toast.success("Saved");
  };
  return (
    <Card className="p-4 max-w-xl">
      <div className="text-xs bg-amber-50 border border-amber-200 p-2 rounded mb-3" data-testid="billing-source-banner">
        Current effective source: <strong>{src}</strong>. To make this DB-stored creditor authoritative, set env <code>SEPA_CREDITOR_CONFIG=db</code> and restart the backend.
      </div>
      {["name","nif","iban","bic","creditor_id"].map(k => (
        <div key={k} className="mb-2">
          <label className="text-xs uppercase font-semibold text-slate-500">{k}</label>
          <Input value={c[k]||""} onChange={(e)=>setC({...c, [k]: e.target.value})} data-testid={`billing-input-${k}`}/>
        </div>
      ))}
      <Button onClick={save} className="bg-[#0A1F44] mt-2" data-testid="billing-save">Save</Button>
    </Card>
  );
}

function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const load = () => http.get("/admin/email-templates").then(({data}) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const { key, ...rest } = active;
    await http.put(`/admin/email-templates/${key}`, rest);
    toast.success("Template saved"); setActive(null); load();
  };
  return (
    <Card className="p-4">
      <table className="w-full text-sm" data-testid="templates-table">
        <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-2">Trigger</th><th className="text-left">Subject (EN)</th><th></th></tr></thead>
        <tbody className="divide-y">
          {items.map(it => (
            <tr key={it.key}>
              <td className="py-2 font-mono text-xs">{it.key}</td>
              <td className="py-2">{it.subject_en}</td>
              <td className="py-2 text-right"><Button size="sm" variant="ghost" onClick={()=>setActive(it)} data-testid={`tpl-edit-${it.key}`}>Edit</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Dialog open={!!active} onOpenChange={(o)=>!o && setActive(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{active?.key}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-2">
              {["subject_en","subject_es","body_en","body_es"].map(f => f.startsWith("subject") ? (
                <div key={f}><label className="text-xs">{f}</label><Input value={active[f]||""} onChange={(e)=>setActive({...active, [f]: e.target.value})} data-testid={`tpl-${f}`}/></div>
              ) : (
                <div key={f}><label className="text-xs">{f}</label><Textarea value={active[f]||""} onChange={(e)=>setActive({...active, [f]: e.target.value})} rows={4} data-testid={`tpl-${f}`}/></div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setActive(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0A1F44]" data-testid="tpl-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TriggersTab() {
  const [items, setItems] = useState([]);
  const load = () => http.get("/admin/triggers").then(({data}) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const toggle = async (key, ch, val) => {
    const row = items.find(i => i.key === key);
    const next = { ...row.channels, [ch]: val };
    await http.patch("/admin/triggers", { key, channels: next });
    load();
  };
  const testFire = async (key) => {
    try { await http.post(`/admin/triggers/${key}/test`); toast.success("Test sent (MOCKED)"); }
    catch { toast.error("Failed"); }
  };
  return (
    <Card className="p-4">
      <table className="w-full text-sm" data-testid="triggers-table">
        <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-2">Trigger</th><th>email</th><th>sms</th><th>whatsapp</th><th>in_app</th><th></th></tr></thead>
        <tbody className="divide-y">
          {items.map(it => (
            <tr key={it.key}>
              <td className="py-2 font-mono text-xs">{it.key}</td>
              {["email","sms","whatsapp","in_app"].map(ch => (
                <td key={ch} className="text-center"><Checkbox checked={!!it.channels[ch]} onCheckedChange={(v)=>toggle(it.key, ch, !!v)} data-testid={`trig-${it.key}-${ch}`}/></td>
              ))}
              <td className="text-right"><Button size="sm" variant="ghost" onClick={()=>testFire(it.key)} data-testid={`trig-test-${it.key}`}>Test</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function HealthTab() {
  const [h, setH] = useState(null);
  const recheck = async () => {
    setH(null);
    const { data } = await http.get("/admin/integrations/health");
    setH(data);
  };
  useEffect(() => { recheck(); }, []);
  return (
    <Card className="p-4">
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold">Integrations health</h3>
        <Button size="sm" variant="outline" onClick={recheck} data-testid="health-recheck"><RefreshCw className="h-4 w-4 mr-1"/>Re-check</Button>
      </div>
      {!h ? <div className="text-slate-400">Loading…</div> : (
        <div className="space-y-2" data-testid="health-list">
          {Object.entries(h).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 border rounded px-3 py-2" data-testid={`health-row-${k}`}>
              {v.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600"/> : <AlertCircle className="h-4 w-4 text-rose-600"/>}
              <div className="flex-1">
                <div className="font-medium capitalize">{k} <Badge variant="outline" className="ml-2 text-xs">{v.backend}</Badge></div>
                <div className="text-xs text-slate-500">{v.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function BackupsTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => http.get("/admin/backups").then(({data}) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy(true);
    try {
      await http.post("/admin/backups/run-now");
      toast.success("Backup completed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    }
    setBusy(false);
  };
  const download = async (b) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.token");
    const r = await fetch(`${base}/api/admin/backups/${b.id}/download`,
      { headers: { Authorization: `Bearer ${token}` }});
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = b.filename || `backup_${b.id}.gz`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-[#0A1F44]"/>
          <h3 className="font-medium">Automated daily backups</h3>
        </div>
        <Button onClick={runNow} disabled={busy} className="bg-[#0A1F44]" data-testid="backups-run-now">
          <Play className="h-4 w-4 mr-1"/>
          {busy ? "Running…" : "Run now"}
        </Button>
      </div>
      <div className="text-xs text-slate-500 mb-3">
        Backups run daily at 03:00 Europe/Madrid via the SharePoint backend (MOCKED until Phase 6 swap). Retention: last 30 successful runs.
      </div>
      <table className="w-full text-sm" data-testid="backups-table">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b">
          <tr>
            <th className="text-left p-2">Timestamp</th>
            <th className="text-left p-2">Status</th>
            <th className="text-left p-2">Size</th>
            <th className="text-left p-2">Triggered by</th>
            <th className="p-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={5} className="text-center p-4 text-slate-400">No backups yet.</td></tr>
          )}
          {items.map(b => (
            <tr key={b.id} className="border-b hover:bg-slate-50" data-testid={`backup-row-${b.id}`}>
              <td className="p-2 font-mono text-xs">{b.ts?.slice(0,19).replace("T", " ")}</td>
              <td className="p-2">
                <Badge className={b.status === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>
                  {b.status === "ok" ? <CheckCircle2 className="h-3 w-3 inline mr-1"/> : <AlertCircle className="h-3 w-3 inline mr-1"/>}
                  {b.status}
                </Badge>
              </td>
              <td className="p-2 text-xs text-slate-600">{b.size_bytes ? `${(b.size_bytes/1024).toFixed(1)} KB` : "—"}</td>
              <td className="p-2 text-xs text-slate-600">{b.triggered_by}</td>
              <td className="p-2 text-right">
                {b.status === "ok" && (
                  <Button size="sm" variant="ghost" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}>
                    <Download className="h-4 w-4"/>
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

